import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';

type ClientSegment = {
  id?: string | number | null;
  brandId: string | number;
  name?: string | null;
};

const SELECT_SKELETON_HEIGHT_REM = '2.5rem';

export interface ClientSegmentSelectorProps {
  segments: ClientSegment[];
  loading: boolean;
  selectedSegmentId?: string | number | null;
  onSegmentChange?: (segmentId: string | null) => boolean | Promise<boolean | void>;
  disabled?: boolean;
  className?: string;
}

export const ClientSegmentSelector = ({
  segments,
  loading,
  selectedSegmentId,
  onSegmentChange,
  disabled = false,
  className,
}: ClientSegmentSelectorProps) => {
  const [internalValue, setInternalValue] = useState('');
  // Guards against stale async completions (e.g., confirmation modal resolves after unmount).
  // Incrementing this invalidates any in-flight `tryApplySegment` call that started earlier.
  const segmentChangeTokenRef = useRef(0);

  const currentValue = internalValue ?? '';
  const hasSegments = segments.length > 0;

  useEffect(() => {
    if (typeof selectedSegmentId === 'number' || typeof selectedSegmentId === 'string') {
      setInternalValue(String(selectedSegmentId));
      return;
    }

    setInternalValue('');
  }, [selectedSegmentId]);

  useEffect(() => {
    return () => {
      segmentChangeTokenRef.current += 1;
    };
  }, []);

  const canProceedWith = useCallback(
    async (segmentId: string | null) => {
      return (await onSegmentChange?.(segmentId)) ?? true;
    },
    [onSegmentChange],
  );

  const tryApplySegment = useCallback(
    async (segmentId: string | null, internalValueToSet?: string) => {
      const token = segmentChangeTokenRef.current + 1;
      segmentChangeTokenRef.current = token;
      const canProceed = await canProceedWith(segmentId);
      if (!canProceed) {
        return false;
      }

      if (token !== segmentChangeTokenRef.current) {
        return false;
      }

      setInternalValue(internalValueToSet ?? (segmentId ? String(segmentId) : ''));
      return true;
    },
    [canProceedWith],
  );

  const handleSegmentChange = useCallback(
    async (event: SelectChangeEvent) => {
      const nextValue = event.target.value;
      const normalizedValue = nextValue ? nextValue : null;

      await tryApplySegment(normalizedValue, nextValue);
    },
    [tryApplySegment],
  );

  const selectedLabel = useMemo(() => {
    if (!currentValue) {
      return 'Select a client segment';
    }

    return (
      segments.find((segment) => String(segment.brandId) === currentValue)?.name ??
      'Untitled segment'
    );
  }, [currentValue, segments]);

  let body: ReactNode;

  if (loading) {
    body = <Skeleton variant="rounded" height={SELECT_SKELETON_HEIGHT_REM} />;
  } else if (!hasSegments) {
    body = (
      <Typography variant="body2" color="text.secondary">
        No client segments are configured for this partner yet.
      </Typography>
    );
  } else {
    body = (
      <FormControl fullWidth size="small" disabled={disabled}>
        <Select
          value={currentValue}
          onChange={handleSegmentChange}
          displayEmpty
          renderValue={(value) => (value ? selectedLabel : 'Select a client segment')}
        >
          {segments.map((segment) => (
            <MenuItem key={segment.id ?? segment.brandId} value={String(segment.brandId)}>
              {segment.name || 'Untitled segment'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <Stack spacing={1} className={className}>
      <Typography variant="caption" sx={panelFieldLabelSx}>
        Client segment
      </Typography>
      {body}
    </Stack>
  );
};
